/* tslint:disable */
/* eslint-disable */
/**
 * iEduBiofoundry API
 * **Namespacing.** Routes `/api/learn/_*`, tables `learn_*`, Python module `app/learning/`, Python classes prefixed `Learn*`. Within this namespace, \"Session\" means a *learning* Session — a facilitator-created event — never the research workspace in `app/models.py`.  **Three principals, never overlapping:**   * Facilitator — existing oauth2-proxy cookie or `X-API-Key`.   * Learner — `X-Learner-Token`, Visit-scoped, short TTL, heartbeat-renewed. Can write.   * Take-home — capability URL `/learn/me/{token}`. Read-only.  **Game data is opaque.** `LearnAttempt.state` and `LearnArtifact.payload` are arbitrary JSON owned by the frontend. The backend stores and returns them without inspection.  **The two learner tokens are stored differently, on purpose.** The visit token is hashed — it writes, it is short-lived, nobody needs it back. The take-home token is stored recoverably and is only seven characters, because it is read-only and is typed by hand off a printed card; the trade is spelled out in `API_DRAFT.md` §1 and D16.  **Call-by-call frontend walkthrough:** `API_WALKTHROUGH.md` — one Session, several Visits, every exchange in order, including the failure modes.  **Status.** Hand-authored ahead of implementation. Once `app/learning/` exists, FastAPI\'s generated schema becomes the source of truth and this file should be regenerated from it. 
 *
 * The version of the OpenAPI document: 0.1.0-draft
 * Contact: mjberry@illinois.edu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import * as runtime from '../runtime';
import {
    type CreateLearnVisit201Response,
    CreateLearnVisit201ResponseFromJSON,
    CreateLearnVisit201ResponseToJSON,
} from '../models/CreateLearnVisit201Response';
import {
    type LearnVisitCreate,
    LearnVisitCreateFromJSON,
    LearnVisitCreateToJSON,
} from '../models/LearnVisitCreate';
import {
    type PublicLearnSession,
    PublicLearnSessionFromJSON,
    PublicLearnSessionToJSON,
} from '../models/PublicLearnSession';
import {
    type RateLimitError,
    RateLimitErrorFromJSON,
    RateLimitErrorToJSON,
} from '../models/RateLimitError';

export interface CreateLearnVisitRequest {
    /**
     * 
     */
    learnVisitCreate: LearnVisitCreate;
}

export interface GetLearnSessionByCodeRequest {
    /**
     * 
     */
    joinCode: string;
}

/**
 * 
 */
export class PublicApi extends runtime.BaseAPI {

    /**
     * Creates request options for createLearnVisit without sending the request
     */
    async createLearnVisitRequestOpts(requestParameters: CreateLearnVisitRequest): Promise<runtime.RequestOpts> {
        if (requestParameters['learnVisitCreate'] == null) {
            throw new runtime.RequiredError(
                'learnVisitCreate',
                'Required parameter "learnVisitCreate" was null or undefined when calling createLearnVisit().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';


        let urlPath = `/learn/visits`;

        return {
            path: urlPath,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: LearnVisitCreateToJSON(requestParameters['learnVisitCreate']),
        };
    }

    /**
     * Creates a Visit and mints its Visit token. Omit `join_code` for solo \"play alone\" — the Visit is then created with `session_id: null`.  The `visit_token` is returned **once** and never again; only its hash is stored.  A `429` here carries `detail: join_rate_limited` — per-IP, and weak at a venue where thirty tablets share one egress address (`API_DRAFT.md` §6). 
     * Start a visit
     */
    async createLearnVisitRaw(requestParameters: CreateLearnVisitRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<CreateLearnVisit201Response>> {
        const requestOptions = await this.createLearnVisitRequestOpts(requestParameters);
        const response = await this.request(requestOptions, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => CreateLearnVisit201ResponseFromJSON(jsonValue));
    }

    /**
     * Creates a Visit and mints its Visit token. Omit `join_code` for solo \"play alone\" — the Visit is then created with `session_id: null`.  The `visit_token` is returned **once** and never again; only its hash is stored.  A `429` here carries `detail: join_rate_limited` — per-IP, and weak at a venue where thirty tablets share one egress address (`API_DRAFT.md` §6). 
     * Start a visit
     */
    async createLearnVisit(requestParameters: CreateLearnVisitRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<CreateLearnVisit201Response> {
        const response = await this.createLearnVisitRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Creates request options for getLearnSessionByCode without sending the request
     */
    async getLearnSessionByCodeRequestOpts(requestParameters: GetLearnSessionByCodeRequest): Promise<runtime.RequestOpts> {
        if (requestParameters['joinCode'] == null) {
            throw new runtime.RequiredError(
                'joinCode',
                'Required parameter "joinCode" was null or undefined when calling getLearnSessionByCode().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};


        let urlPath = `/learn/sessions/by-code/{joinCode}`;
        urlPath = urlPath.replace('{joinCode}', encodeURIComponent(String(requestParameters['joinCode'])));

        return {
            path: urlPath,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        };
    }

    /**
     * Returns only what a student needs to render the join screen. Never the owner, the budget caps, or the visit list.  Returns 404 for an unknown code **and** for a code whose session is not `open`, so the response does not disclose that a session exists.  The code is normalized before matching (see `JoinCodeInput`). Unlike `/learn/me/{token}`, this route *does* distinguish `422` from `404`: a wrong join code discloses nothing worth protecting, so there is no reason to collapse its failure shapes.  A `429` here carries `detail: join_rate_limited`. 
     * Resolve a join code
     */
    async getLearnSessionByCodeRaw(requestParameters: GetLearnSessionByCodeRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PublicLearnSession>> {
        const requestOptions = await this.getLearnSessionByCodeRequestOpts(requestParameters);
        const response = await this.request(requestOptions, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PublicLearnSessionFromJSON(jsonValue));
    }

    /**
     * Returns only what a student needs to render the join screen. Never the owner, the budget caps, or the visit list.  Returns 404 for an unknown code **and** for a code whose session is not `open`, so the response does not disclose that a session exists.  The code is normalized before matching (see `JoinCodeInput`). Unlike `/learn/me/{token}`, this route *does* distinguish `422` from `404`: a wrong join code discloses nothing worth protecting, so there is no reason to collapse its failure shapes.  A `429` here carries `detail: join_rate_limited`. 
     * Resolve a join code
     */
    async getLearnSessionByCode(requestParameters: GetLearnSessionByCodeRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PublicLearnSession> {
        const response = await this.getLearnSessionByCodeRaw(requestParameters, initOverrides);
        return await response.value();
    }

}
